import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, MessageCircle, Clock, Send } from 'lucide-react';
import { contactInfo } from '../data/services';

export default function Contact() {
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${contactInfo.whatsapp}?text=Hi, I would like to inquire about your services.`,
      '_blank'
    );
  };

  return (
    <main className="min-h-screen pt-20 bg-[#dbeafe]" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#dbeafe] to-[#bfdbfe] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block text-blue-500 font-medium text-sm uppercase tracking-wider mb-3">
              Get In Touch
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 font-['Oswald']">
                Contact Us
            </h1>
            <p className="text-lg text-slate-700">
              Have questions or need to book a service? We're here to help! 
              Reach out to us through any of the channels below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Phone Card */}
            <motion.a
              href={`tel:+91${contactInfo.phone}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow group cursor-pointer border border-blue-100"
              data-testid="contact-phone-card"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                <Phone size={28} className="text-blue-500 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">Call Us</h3>
              <p className="text-slate-700">+91 {contactInfo.phone}</p>
            </motion.a>

            {/* WhatsApp Card */}
            <motion.button
              onClick={handleWhatsApp}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow group cursor-pointer text-left border border-blue-100"
              data-testid="contact-whatsapp-card"
            >
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-green-500 transition-colors">
                <MessageCircle size={28} className="text-green-500 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">WhatsApp</h3>
              <p className="text-slate-700">Chat with us instantly</p>
            </motion.button>

            {/* Email Card */}
            <motion.a
              href={`mailto:${contactInfo.email}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow group cursor-pointer border border-blue-100"
              data-testid="contact-email-card"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                <Mail size={28} className="text-blue-500 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">Email Us</h3>
              <p className="text-slate-700 text-sm break-all">{contactInfo.email}</p>
            </motion.a>

            {/* Address Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-blue-100"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-6">
                <MapPin size={28} className="text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">Visit Us</h3>
              <p className="text-slate-700 text-sm">{contactInfo.address}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Contact Section */}
      <section className="py-16 bg-[#bfdbfe]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 font-['Oswald']">
                  We're Here to Help
                </h2>
                <p className="text-slate-800">
                  Whether you need AC installation, plumbing services, or home cleaning, 
                  our team is ready to assist you. Contact us now for quick and reliable service.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={22} className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Working Hours</h4>
                    <p className="text-slate-700 text-sm">Monday - Sunday: 8:00 AM - 8:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Send size={22} className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Quick Response</h4>
                    <p className="text-slate-700 text-sm">We respond to all inquiries within 30 minutes</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <a
                  href={`https://wa.me/${contactInfo.whatsapp}?text=Hi, I would like to inquire about your services.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold transition-colors"
                  data-testid="contact-whatsapp-btn"
                >
                  <MessageCircle size={20} />
                  WhatsApp Us
                </a>
                <a
                  href={`tel:+91${contactInfo.phone}`}
                  className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-full font-semibold transition-colors"
                  data-testid="contact-call-btn"
                >
                  <Phone size={20} />
                  Call Now
                </a>
              </div>
            </motion.div>

            {/* Right - Map/Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white rounded-3xl overflow-hidden h-[400px] flex items-center justify-center border border-blue-100">
                {/* Placeholder for map - showing address info */}
                <div className="text-center p-8">
                  <MapPin size={64} className="text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Oswald']">Our Location</h3>
                  <p className="text-slate-700">{contactInfo.address}</p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=Machilipatnam,Andhra+Pradesh,India`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-500 hover:text-blue-600 font-medium mt-4"
                  >
                    View on Google Maps
                  </a>
                </div>
              </div>
              {/* Decorative */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500 rounded-2xl -z-10 rotate-6" />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
